/**
 * 쿠팡 마진계산기 계산 로직 (순수 함수 — import 없음)
 *
 * 세전(소득세 이전) 관리용 계산이다. 수수료율·물류비는 쿠팡 정책/카테고리에 따라 다르므로
 * 화면에서 사용자가 입력한 값을 그대로 쓴다(코드에 정책 요율을 박아 두지 않는다).
 *
 * 과세 유형
 *  - GENERAL(일반과세): 판매가(VAT 포함)에서 VAT 를 빼고 공급가액 기준으로 계산.
 *    매입원가가 VAT 포함이면 매입세액 공제를 받는 것으로 보아 공급가액으로 환산.
 *    수수료·물류비·광고비는 "VAT 별도" 금액 입력으로 보고 그대로 비용 처리(그 VAT 는 공제).
 *  - SIMPLE(간이·기타): 공제 불가로 보고 판매가·원가는 VAT 포함 그대로, 수수료·물류비·광고비에는 VAT 10% 가산,
 *    납부 부가세는 판매가 대비 입력 비율(simpleTaxRate)로 차감.
 *
 * 입력 inp (숫자 또는 숫자 문자열):
 *  price 판매가(VAT포함) / cost 개당 매입원가 / costVatIncl 매입원가 VAT 포함 여부 / qtyPerSet 구성수량
 *  feeRate 판매수수료율(%) / shipFee 배송·물류비(건당, VAT별도) / adRate 광고비율(%) / packFee 포장·기타(건당)
 *  returnRate 반품·CS 충당률(%) / taxMode / simpleTaxRate(%)
 */

const n = (v) => {
  const x = Number(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(x) ? x : 0;
};
const pct = (v) => n(v) / 100;

/** 판매가 P 에서의 손익 명세 */
export function calcAt(P, inp) {
  const q = Math.max(1, Math.floor(n(inp.qtyPerSet)) || 1);
  const costTotal = n(inp.cost) * q;
  const rate = pct(inp.feeRate);
  const ad = pct(inp.adRate);
  const ret = pct(inp.returnRate);
  const general = inp.taxMode !== 'SIMPLE';
  const vat = general ? 0 : 0.1;

  const rows = [];
  let profit;
  if (general) {
    const net = P / 1.1;
    const costNet = inp.costVatIncl === false ? costTotal : costTotal / 1.1;
    const fee = P * rate;
    const ship = n(inp.shipFee);
    const adCost = P * ad;
    const pack = n(inp.packFee);
    const retCost = P * ret;
    rows.push(
      { label: '판매가 (VAT 포함)', amount: P, kind: 'base' },
      { label: '(−) 부가세 (판매가 ÷ 11)', amount: -(P - net), kind: 'cost' },
      { label: '공급가액', amount: net, kind: 'sub' },
      { label: `(−) 매입원가 ${q > 1 ? `(${q}개)` : ''}${inp.costVatIncl === false ? '' : ' · VAT 제외'}`, amount: -costNet, kind: 'cost' },
      { label: `(−) 판매수수료 ${n(inp.feeRate)}%`, amount: -fee, kind: 'cost' },
      { label: '(−) 배송·물류비', amount: -ship, kind: 'cost' },
      { label: `(−) 광고비 ${n(inp.adRate)}%`, amount: -adCost, kind: 'cost' },
      { label: '(−) 포장·기타', amount: -pack, kind: 'cost' },
      { label: `(−) 반품·CS 충당 ${n(inp.returnRate)}%`, amount: -retCost, kind: 'cost' }
    );
    profit = net - costNet - fee - ship - adCost - pack - retCost;
  } else {
    const fee = P * rate * (1 + vat);
    const ship = n(inp.shipFee) * (1 + vat);
    const adCost = P * ad * (1 + vat);
    const pack = n(inp.packFee);
    const retCost = P * ret;
    const tax = P * pct(inp.simpleTaxRate);
    rows.push(
      { label: '판매가 (VAT 포함)', amount: P, kind: 'base' },
      { label: `(−) 매입원가 ${q > 1 ? `(${q}개)` : ''}`, amount: -costTotal, kind: 'cost' },
      { label: `(−) 판매수수료 ${n(inp.feeRate)}% (+VAT)`, amount: -fee, kind: 'cost' },
      { label: '(−) 배송·물류비 (+VAT)', amount: -ship, kind: 'cost' },
      { label: `(−) 광고비 ${n(inp.adRate)}% (+VAT)`, amount: -adCost, kind: 'cost' },
      { label: '(−) 포장·기타', amount: -pack, kind: 'cost' },
      { label: `(−) 반품·CS 충당 ${n(inp.returnRate)}%`, amount: -retCost, kind: 'cost' },
      { label: `(−) 납부 부가세 ${n(inp.simpleTaxRate)}%`, amount: -tax, kind: 'cost' }
    );
    profit = P - costTotal - fee - ship - adCost - pack - retCost - tax;
  }
  rows.push({ label: '순이익 (세전)', amount: profit, kind: 'total' });

  return {
    price: P,
    profit,
    margin: P > 0 ? profit / P : 0, // 판매가 대비 마진율
    roi: costTotal > 0 ? profit / costTotal : 0, // 원가 대비 수익률
    costTotal,
    rows,
  };
}

/** 판매가에 대해 손익은 선형(profit = a·P − k)이라 두 점으로 기울기·고정비를 구한다 */
function linear(inp) {
  const p0 = calcAt(0, inp).profit;
  const p1 = calcAt(1, inp).profit;
  return { a: p1 - p0, k: -p0 };
}

const ceil10 = (v) => Math.ceil(v / 10) * 10;

/**
 * 손익분기 판매가 / 목표 마진율 달성 판매가
 * @returns {{breakEven:number|null, target:number|null}} 불가능하면 null (수수료·광고비 등 변동비율이 너무 커서)
 */
export function solvePrices(inp, targetMarginPct) {
  const { a, k } = linear(inp);
  const m = pct(targetMarginPct);
  return {
    breakEven: a > 0 ? ceil10(k / a) : null,
    target: a - m > 0 ? ceil10(k / (a - m)) : null,
  };
}

/** 판매가 시나리오(±비율) 표 */
export function priceLadder(inp, steps = [-0.1, -0.05, 0, 0.05, 0.1]) {
  const base = n(inp.price);
  return steps.map((s) => {
    const P = Math.round((base * (1 + s)) / 10) * 10;
    const r = calcAt(P, inp);
    return { step: s, price: P, profit: r.profit, margin: r.margin, roi: r.roi };
  });
}
